import { useState } from "react";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { ICupom } from "../../pedidos/interface/ICupom";
import { TipoCupom } from "../../pedidos/enum/TipoCupom";
import { TipoMarketplace } from "../../marketplace/enum/TipoMarketplace";
import { IClienteProgramaFidelidade } from "../../clientes/interface/IClienteProgramaFidelidade";
import { IProdutoPedido } from "../../pedidos/interface/IProdutoPedido";
import { v4 } from "uuid";
import { calcularValorTotalProduto } from "../../pedidos/utils/calcularValorTotalProduto";

interface UseResgateFidelidadeProps {
    aplicarCupom: (cupom: ICupom) => Promise<void>;
    cupons: ICupom[];
}

export const useResgateFidelidade = ({
    aplicarCupom,
    cupons
}: UseResgateFidelidadeProps) => {
    const snackbar = useSnackbar();
    const [resgatandoCashback, setResgatandoCashback] = useState<string | null>(null);
    const [resgatandoItens, setResgatandoItens] = useState<string | null>(null);

    const verificarJaResgatado = (programaFidelidadeUuid: string): boolean => {
        // Verificar se existe um cupom principal (Itens Fidelidade ou Cashback Fidelidade) 
        // com o mesmo programaFidelidadeUuid
        return cupons.some(
            (cupom) => 
                cupom.programaFidelidadeUuid === programaFidelidadeUuid &&
                (cupom.nome === "Itens Fidelidade" || cupom.nome === "Cashback Fidelidade")
        );
    };

    const resgatarCashback = async (programaCliente: IClienteProgramaFidelidade) => {
        if (!programaCliente.programaFidelidadeUuid) {
            snackbar("Programa de fidelidade inválido", { severity: "error" });
            return;
        }

        if (verificarJaResgatado(programaCliente.programaFidelidadeUuid)) {
            snackbar("Este benefício já foi resgatado", { severity: "warning" });
            return;
        }

        if (!programaCliente.saldoReais || programaCliente.saldoReais <= 0) {
            snackbar("Saldo de cashback insuficiente", { severity: "error" });
            return;
        }

        setResgatandoCashback(programaCliente.programaFidelidadeUuid);

        try {
            const cupom: ICupom = {
                tipo: TipoCupom.TOTAL,
                valor: programaCliente.saldoReais,
                programaFidelidadeUuid: programaCliente.programaFidelidadeUuid,
                codigo: `FIDELIDADE-${programaCliente.programaFidelidadeUuid}-${Date.now()}`,
                nome: "Cashback Fidelidade",
                descricao: `Resgate de cashback do programa de fidelidade`,
                ativo: true,
                marketplaces: [{ tipo: TipoMarketplace.PDV }],
            };

            await aplicarCupom(cupom);
            snackbar("Cashback resgatado com sucesso!", { severity: "success" });
        } catch (error) {
            snackbar("Erro ao resgatar cashback", { severity: "error" });
        } finally {
            setResgatandoCashback(null);
        }
    };

    const resgatarItens = async (
        programaCliente: IClienteProgramaFidelidade
    ) => {
        if (!programaCliente.programaFidelidadeUuid) {
            snackbar("Programa de fidelidade inválido", { severity: "error" });
            return;
        }

        if (verificarJaResgatado(programaCliente.programaFidelidadeUuid)) {
            snackbar("Este benefício já foi resgatado", { severity: "warning" });
            return;
        }

        if (!programaCliente.saldoPedidos) {
            snackbar("Saldo de pedidos insuficiente", { severity: "error" });
            return;
        }

        setResgatandoItens(programaCliente.programaFidelidadeUuid);

        try {
            const cupom: ICupom = {
                tipo: TipoCupom.TOTAL,
                valor: 0,
                programaFidelidadeUuid: programaCliente.programaFidelidadeUuid,
                codigo: `FIDELIDADE-${programaCliente.programaFidelidadeUuid}-${Date.now()}`,
                nome: "Itens Fidelidade",
                descricao: `Resgate de itens do programa de fidelidade`,
                ativo: true,
                marketplaces: [{ tipo: TipoMarketplace.PDV }],
            };

            await aplicarCupom(cupom);
            snackbar("Itens resgatados com sucesso!", { severity: "success" });
        } catch (error) {
            snackbar("Erro ao resgatar os itens", { severity: "error" });
        } finally {
            setResgatandoItens(null);
        }
    };

    const resgatarItem = async (
        programaCliente: IClienteProgramaFidelidade,
        produto: IProdutoPedido
    ) => {
        if (!programaCliente.programaFidelidadeUuid) {
            snackbar("Programa de fidelidade inválido", { severity: "error" });
            return;
        }

        if (!programaCliente.saldoPedidos) {
            snackbar("Saldo de pedidos insuficiente", { severity: "error" });
            return;
        }

        try {
            // Calcula o valor total do produto incluindo complementos, bordas, massas e sabores
            const valorTotalProduto = calcularValorTotalProduto(produto);

            const cupom: ICupom = {
                tipo: TipoCupom.TOTAL,
                valor: valorTotalProduto,
                programaFidelidadeUuid: programaCliente.programaFidelidadeUuid,
                codigo: `FIDELIDADE-${produto.descricao}-${v4()}-${produto.uuidProdutoReferencia || produto.uuid}`,
                nome: `Item fidelidade (${produto.descricao})`,
                descricao: `Resgate de item do programa de fidelidade`,
                ativo: true,
                marketplaces: [{ tipo: TipoMarketplace.PDV }],
                uuid: v4(),
            };

            await aplicarCupom(cupom);
        } catch (error) {
            snackbar("Erro ao resgatar o item", { severity: "error" });
        }
    };

    return {
        resgatarCashback,
        resgatarItens,
        verificarJaResgatado,
        resgatandoCashback,
        resgatandoItens,
        resgatarItem,
    };
};

